import React, { RefObject } from 'react';
import { Project } from '../../types';

type HeaderBarProps = {
  serverState: 'unknown' | 'ok' | 'error';
  branches: string[];
  activeBranch: string;
  onChangeBranch: (branch: string) => void;
  apiKey: string;
  apiSecret: string;
  apiBase: string;
  onChangeApiKey: (val: string) => void;
  onChangeApiSecret: (val: string) => void;
  onChangeApiBase: (val: string) => void;
  onResetApiBase: () => void;
  autoSaveEnabled: boolean;
  onToggleAutosave: () => void;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  projects: Project[];
  activeProject: string;
  projectOverride: string;
  onChangeProject: (projectId: string) => void;
  onChangeProjectOverride: (val: string) => void;
  onApplyProjectOverride: () => void;
  commitMessage: string;
  onChangeCommitMessage: (val: string) => void;
  onCommit: () => void;
  onExportSettings: () => void;
  onImportSettings: () => void;
  onImportFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  importSettingsInputRef: RefObject<HTMLInputElement | null>;
  onReloadProject: () => void;
  onClearCache: () => void;
  onSave: () => void;
  onRunAll: () => void;
  taskRunning: string | null;
  isFetchingTree: boolean;
};

export const HeaderBar = React.memo(function HeaderBar({
  serverState,
  branches,
  activeBranch,
  onChangeBranch,
  apiKey,
  apiSecret,
  apiBase,
  onChangeApiKey,
  onChangeApiSecret,
  onChangeApiBase,
  onResetApiBase,
  autoSaveEnabled,
  onToggleAutosave,
  theme,
  onToggleTheme,
  projects,
  activeProject,
  projectOverride,
  onChangeProject,
  onChangeProjectOverride,
  onApplyProjectOverride,
  commitMessage,
  onChangeCommitMessage,
  onCommit,
  onExportSettings,
  onImportSettings,
  onImportFileChange,
  importSettingsInputRef,
  onReloadProject,
  onClearCache,
  onSave,
  onRunAll,
  taskRunning,
  isFetchingTree,
}: HeaderBarProps) {
  return (
    <header className="top-bar">
      <div className="brand">
        <div className="brand-mark">
          <span className="mark-outer" />
          <span className="mark-inner" />
        </div>
        <div>
          <p className="eyebrow">React workspace</p>
          <h1>Atlas IDE</h1>
        </div>
      </div>
      <div className="header-actions">
        <div className="status-group">
          <span className={`pill ${serverState === 'error' ? 'error' : 'success'}`}>
            dev server · {serverState === 'error' ? 'down' : serverState === 'ok' ? 'ready' : 'checking'}
          </span>
          <div className="branch-switcher">
            <select
              value={activeBranch}
              onChange={(e) => {
                onChangeBranch(e.target.value);
              }}
            >
              {branches.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="api-creds">
            <input
              className="api-base-input"
              type="text"
              value={apiKey}
              placeholder="API key"
              onChange={(e) => onChangeApiKey(e.target.value)}
            />
            <input
              className="api-base-input"
              type="password"
              value={apiSecret}
              placeholder="API secret"
              onChange={(e) => onChangeApiSecret(e.target.value)}
            />
          </div>
          <div className="api-base">
            <input
              className="api-base-input"
              type="text"
              value={apiBase}
              placeholder="API base URL"
              onChange={(e) => onChangeApiBase(e.target.value)}
            />
            <button className="pill-btn ghost-btn" onClick={onResetApiBase} type="button">
              Reset API
            </button>
          </div>
          <button className={`pill subtle ${autoSaveEnabled ? 'success' : 'outline'}`} onClick={onToggleAutosave} type="button">
            autosave {autoSaveEnabled ? 'on' : 'off'}
          </button>
          <button className="pill subtle" onClick={onToggleTheme} type="button">
            theme {theme}
          </button>
        </div>
        <div className="cta-group">
          <div className="project-switcher">
            <select
              value={activeProject}
              onChange={(e) => {
                const next = e.target.value;
                onChangeProject(next);
              }}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="project-override">
            <input
              className="api-base-input"
              type="text"
              value={projectOverride}
              placeholder="Project ID override"
              onChange={(e) => onChangeProjectOverride(e.target.value)}
            />
            <button className="pill-btn" type="button" onClick={onApplyProjectOverride}>
              Apply project
            </button>
            <button className="pill-btn ghost-btn" type="button" onClick={onExportSettings}>
              Export settings
            </button>
            <button className="pill-btn ghost-btn" type="button" onClick={onImportSettings}>
              Import settings
            </button>
            <input
              ref={importSettingsInputRef}
              type="file"
              accept="application/json"
              style={{ display: 'none' }}
              onChange={onImportFileChange}
            />
          </div>
          <div className="commit-box">
            <input
              type="text"
              placeholder="Commit message"
              value={commitMessage}
              onChange={(e) => onChangeCommitMessage(e.target.value)}
            />
            <button className="primary" onClick={onCommit}>
              Commit
            </button>
          </div>
          <button className="ghost" onClick={onReloadProject} disabled={isFetchingTree}>
            {isFetchingTree ? 'Refreshing…' : 'Reload project'}
          </button>
          <button className="ghost" onClick={onClearCache}>
            Clear cache
          </button>
          <button className="ghost" onClick={onSave}>
            Save
          </button>
          <button className="primary" onClick={onRunAll} disabled={!!taskRunning}>
            {taskRunning ? `Running ${taskRunning}…` : 'Run checks'}
          </button>
        </div>
      </div>
    </header>
  );
});
